import { useState, useCallback } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { toast } from 'react-toastify';
import { QueryGroup, QuerySource } from '../../types/QueryBuilder';
import { convertGroupsToBackendFormat } from '../../utils/queryBuilderUtils';
import { useQueryBuilderCache } from '../../context/QueryBuilderCacheContext';

interface UpdateQueryParams {
    queryId: string | number;
    description: string;
    groups: QueryGroup[];
    groupId?: number | null;
    source?: QuerySource;
    silent?: boolean;
}

interface UpdateQueryResult {
    queryId: number;
    status: string;
}

export const useUpdateQuery = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const cache = useQueryBuilderCache();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const updateQuery = useCallback(async ({ queryId, description, groups, groupId, source, silent = false }: UpdateQueryParams): Promise<UpdateQueryResult | null> => {
        if (!isReady || !groups?.length || !groups.some(g => g.rules?.length)) return null;

        setLoading(true);
        setError(null);
        const toastId = silent ? null : toast.loading('Modification de la requête en cours...');

        try {
            const response = await authFetch('/saveQuery', {
                method: 'POST',
                body: JSON.stringify({
                    query_id: queryId,
                    queryName: description,
                    description,
                    query_type: 1,
                    source: source ?? 'SIMPLE',
                    conditions: [convertGroupsToBackendFormat(groups)],
                    ...(groupId && groupId > 0 && { group_query_id: groupId }),
                }),
            });

            if (!response?.status && !response?.queryId) throw new Error('Réponse invalide du serveur');

            cache?.invalidate('saved_queries|');
            if (toastId) {
                toast.update(toastId, {
                    render: 'Requête mise à jour avec succès',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000,
                });
            }
            return { queryId: Number(response.queryId ?? queryId), status: response.status ?? 'updated' };
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
            setError(message);
            if (toastId) {
                toast.update(toastId, {
                    render: message,
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                });
            }
            return null;
        } finally {
            setLoading(false);
        }
    }, [authFetch, isReady, cache]);

    return { updateQuery, loading, error };
};
